import { useMemo } from 'react';
import { useInstallationPort } from '@taki/ui';
import type { ShellAppEntry } from './types.js';

/**
 * LES APPS INSTALLEES, PARMI CELLES QUE CE BUNDLE SAIT SERVIR.
 *
 * Le routeur du shell connait TOUTES les entrees du registre de l'hote (il se
 * construit une fois, et une app qu'on installe depuis Parametres doit s'ouvrir
 * sans le reconstruire). Ce qui s'AFFICHE (dock, menu Home, hub, journal,
 * Studio, sauts entre apps) ne garde que les apps de la liste de l'org,
 * `orgs/{orgId}/settings/apps`, lue par le port d'installation.
 *
 * L'ORDRE EST CELUI DU DOCUMENT : il decide celui du dock. Un id que ce bundle
 * ne connait pas (une app retiree du produit, un document ecrit par une version
 * plus recente) n'est monte nulle part : Parametres le montre, a desinstaller.
 * Sans port, la liste est vide.
 */
export function entreesInstallees(
  ids: readonly string[],
  toutes: readonly ShellAppEntry[],
): ShellAppEntry[] {
  const parId = new Map(toutes.map((entree) => [entree.id, entree]));
  const rendues: ShellAppEntry[] = [];
  for (const id of ids) {
    const entree = parId.get(id);
    if (entree !== undefined && !rendues.includes(entree)) rendues.push(entree);
  }
  return rendues;
}

/** Les ids installes que ce bundle ne sait pas servir. */
export function idsInconnus(ids: readonly string[], toutes: readonly ShellAppEntry[]): string[] {
  const connus = new Set(toutes.map((entree) => entree.id));
  return ids.filter((id) => !connus.has(id));
}

/** La liste installee, a l'identite stable tant que ni la liste ni le registre ne bougent. */
export function useEntreesInstallees(toutes: readonly ShellAppEntry[]): ShellAppEntry[] {
  const apps = useInstallationPort()?.apps;
  return useMemo(() => entreesInstallees(apps ?? [], toutes), [apps, toutes]);
}
